import { useLayoutEffect, useRef, useState } from 'react';
import cx from 'classnames';
import MuxPlaceholderSplash from './components/MuxPlaceholderSplash/MuxPlaceholderSplash';
import musicLessonInformation from './musicLessonInformation';
import videoInformation from './videoInformation';
import styles from './SCSS/VideoGallery.module.scss';
import libraryStyles from './SCSS/Library.module.scss';

interface VideoCardProps {
  caption: string;
  duration: string;
  children: unknown;
}

const ResourceSection = (props) => {
  const { title, children } = props;
  return (
    <div className={styles.resourceSection} id={props.id}>
      <div className={styles.title}>{title}</div>
      <div className={styles.children}>{children}</div>
    </div>
  );
};

const VideoCard = ({ caption, duration, children }: VideoCardProps): JSX.Element => {
  const cardNode = useRef(null);
  const [showExpander, setShowExpander] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  useLayoutEffect(() => {
    if (isOverflown(cardNode.current)) {
      setShowExpander(true);
    } else {
      setShowExpander(false);
    }

    function isOverflown(element) {
      if (element) {
        const overflow = element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth;

        /* DEBUG
        console.debug(
          `${caption} overflow is `,
          overflow,
          `scrollHeight = `,
          element.scrollHeight,
          ` clientHeight = `,
          element.clientHeight
        );
        */
        return overflow;
      }

      return false;
    }
    // cardNode.current is being added here as a dependency
    // in order to try to get the "show more" prompt to
    // appear. However, it still doesn't show up some of
    // the time
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [setShowExpander, caption, cardNode.current]);
  return (
    <div
      className={cx(styles.videoCard, {
        [`${styles.expand}`]: isExpanded,
        [`${styles.fadeText}`]: showExpander && !isExpanded,
      })}
      ref={cardNode}
    >
      <div className={styles.caption}>
        {caption}
        <span className={styles.lessImportant}> | {duration}</span>
      </div>
      <div className={styles.childrenContainer}>{children}</div>
      <div
        className={cx(styles.expander, {
          [`${styles.moreLink}`]: !isExpanded,
          [`${styles.lessLink}`]: isExpanded,
          [`${styles.hideLink}`]: !showExpander,
        })}
        onClick={handleExpand}
      >
        {isExpanded ? 'Show Less' : 'Show More'}
      </div>
    </div>
  );

  function handleExpand(e) {
    setIsExpanded(!isExpanded);
  }
};

const Description = ({ children }): JSX.Element => {
  return <div className={styles.cardDescription}>{children}</div>;
};

const VideoGallery = (): JSX.Element => {
  return (
    <div className='anonContainer'>
      <div className={styles.boldTitle}>Video Lessons</div>
      <nav className={styles.navbar}>
        <a className={libraryStyles.textLink} href='#musicLessons'>
          Music Lessons
        </a>{' '}
        |{' '}
        <a className={libraryStyles.textLink} href='#readAloudLessons'>
          Read Aloud Lessons
        </a>
      </nav>
      <div className={styles.content} id='musicLessons'>
        <ResourceSection title='Music Lessons' id='music'>
          {musicLessonInformation.map((i, index) => (
            <VideoCard caption={i.title} duration={i.duration} key={`music-${index}`}>
              <MuxPlaceholderSplash videoId={i.assetId} title={i.title} graphicUrl={i.graphicUrl} />
              <Description>{i.description()}</Description>
            </VideoCard>
          ))}
        </ResourceSection>
      </div>
      <div className={styles.content} id='readAloudLessons'>
        <ResourceSection title='Read Aloud Lessons (Level E-F)' id='readAloud'>
          {videoInformation.map((i, index) => (
            <VideoCard caption={i.title} duration={i.duration} key={`readAloud-${index}`}>
              <MuxPlaceholderSplash videoId={i.assetId} title={i.title} graphicUrl={i.graphicUrl} />
              <Description>{i.description()}</Description>
            </VideoCard>
          ))}
        </ResourceSection>
      </div>
    </div>
  );
};

export default VideoGallery;
